import { DateField } from "./DateField";
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getDailyAttendance, getWeeklyAttendance, getMonthlyAttendance, AttendanceRecord, AttendanceSummary, ChartData } from '../services/attendanceService';
import logo from '../assets/logo.jpeg';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import AttendanceHeatmap from './AttendanceHeatmap';
import { getReportMonth, getReportToday, getReportWeekRange } from '../utils/reportDate';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AttendanceDashboard: React.FC = () => {
  const [tab, setTab] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [selectedDate, setSelectedDate] = useState<string>(getReportToday);
  const [weekRange, setWeekRange] = useState<{start: string, end: string}>(() => getReportWeekRange());
  const [selectedMonth, setSelectedMonth] = useState<string>(getReportMonth);
  const [summary, setSummary] = useState<AttendanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberFilter, setMemberFilter] = useState<string>('');

  // Get unique members for dropdown
  const uniqueMembers = useMemo(() => {
    if (!summary?.records) return [];
    
    const memberMap = new Map<string, { id: string; name: string }>();
    summary.records.forEach(record => {
      if (!memberMap.has(record.serialNo)) {
        memberMap.set(record.serialNo, {
          id: record.serialNo,
          name: record.name
        });
      }
    });
    
    return Array.from(memberMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [summary?.records]);

  // Filter records based on member filter
  const filteredRecords = useMemo(() => {
    if (!summary?.records) return [];
    
    if (!memberFilter.trim()) {
      return summary.records;
    }
    
    return summary.records.filter(record => 
      record.serialNo === memberFilter
    );
  }, [summary?.records, memberFilter]);

  // Prepare chart data
  const chartData: ChartData | null = useMemo(() => {
    if (!summary?.records) return null;
    
    if (tab === 'daily') {
      // For daily view, show a simple doughnut chart of present vs absent
      const presentCount = summary.marked;
      const totalEnrolled = summary.enrolled || 0;
      const absentCount = Math.max(0, totalEnrolled - presentCount);
      
      return {
        type: 'doughnut',
        labels: ['Present', 'Absent'],
        datasets: [{
          data: [presentCount, absentCount],
          backgroundColor: ['#4466f2', '#e9ecef'],
          borderColor: ['#4466f2', '#e9ecef'],
          borderWidth: 1,
        }]
      };
    } else {
      // Check if a specific user is filtered
      const isUserFiltered = memberFilter.trim() !== '' && filteredRecords.length > 0;
      
      if (isUserFiltered) {
        // Show heatmap data for the filtered user
        const userAttendanceByDate: { [key: string]: number } = {};
        
        filteredRecords.forEach(record => {
          if (record.date) {
            const date = record.date;
            userAttendanceByDate[date] = (userAttendanceByDate[date] || 0) + 1;
          }
        });
        
        const dates = Object.keys(userAttendanceByDate).sort();
        const counts = dates.map(date => userAttendanceByDate[date]);
        
        return {
          type: 'heatmap',
          labels: dates,
          datasets: [{
            label: 'Attendance Days',
            data: counts,
            backgroundColor: '#4466f2',
            borderColor: '#4466f2',
            borderWidth: 1,
          }],
          userAttendanceData: userAttendanceByDate
        };
      } else {
        // Show bar chart with each user's total attendance count
        const userAttendanceCount: { [key: string]: number } = {};
        
        summary.records.forEach(record => {
          const userName = record.name;
          userAttendanceCount[userName] = (userAttendanceCount[userName] || 0) + 1;
        });
        
        const userNames = Object.keys(userAttendanceCount).sort();
        const attendanceCounts = userNames.map(name => userAttendanceCount[name]);
        
        // Truncate names for labels but keep full names for tooltips
        const truncateNameForChart = (name: string): string => {
          if (name.length <= 15) return name;
          
          const words = name.split(' ');
          if (words.length === 1) {
            return name.substring(0, 12) + '...';
          }
          
          // For multi-word names, use first name + last name initial
          const firstName = words[0];
          const lastInitial = words[words.length - 1].charAt(0);
          
          if (firstName.length <= 10) {
            return `${firstName} ${lastInitial}.`;
          } else {
            return `${firstName.substring(0, 8)}... ${lastInitial}.`;
          }
        };
        
        const truncatedLabels = userNames.map(name => truncateNameForChart(name));
        
        return {
          type: 'bar',
          labels: truncatedLabels,
          datasets: [{
            label: 'Total Attendance',
            data: attendanceCounts,
            backgroundColor: '#4466f2',
            borderColor: '#4466f2',
            borderWidth: 1,
          }],
          fullNames: userNames // Keep full names for tooltips
        };
      }
    }
  }, [summary, tab, memberFilter, filteredRecords]);

  useEffect(() => {
    console.log('Effect triggered with:', { tab, selectedDate, weekRange, selectedMonth });
    fetchAttendance();
    // eslint-disable-next-line
  }, [tab, selectedDate, weekRange.start, weekRange.end, selectedMonth]);

  const fetchAttendance = async () => {
    setLoading(true);
    setError(null);
    try {
      let data: AttendanceSummary | null = null;
      console.log('Fetching attendance data for tab:', tab);
      
      if (tab === 'daily') {
        data = await getDailyAttendance(selectedDate);
      } else if (tab === 'weekly' && weekRange.start && weekRange.end) {
        data = await getWeeklyAttendance(weekRange.start, weekRange.end);
      } else if (tab === 'monthly') {
        data = await getMonthlyAttendance(selectedMonth);
      }
      
      console.log('Received attendance data:', data);
      setSummary(data);
    } catch (e: any) {
      console.error('Error fetching attendance:', e);
      setError(e.message || 'Failed to fetch attendance data. Please try again.');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Date label formatting
  const getDateLabel = () => {
    if (tab === 'daily') {
      return new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
    } else if (tab === 'weekly') {
      if (weekRange.start && weekRange.end) {
        return `${new Date(weekRange.start).toLocaleDateString()} - ${new Date(weekRange.end).toLocaleDateString()}`;
      }
      return 'Select Week';
    } else {
      const [year, month] = selectedMonth.split('-');
      return `${new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'long' })} ${year}`;
    }
  };

  // Week picker helper
  const handleWeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeekRange(getReportWeekRange(e.target.value));
  };

  // Handle tab change
  const handleTabChange = (newTab: 'daily' | 'weekly' | 'monthly') => {
    setTab(newTab);
    // Reset date selections based on tab
    if (newTab === 'daily') {
      setSelectedDate(getReportToday());
    } else if (newTab === 'weekly') {
      setWeekRange(getReportWeekRange());
    } else {
      setSelectedMonth(getReportMonth());
    }
  };

  return (
    <div className="fitness-color nunito-body attendance-dashboard-page">
      {/* Header Section Start */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/dashboard" className="back-button">
              <i className="ri-arrow-left-s-line"></i>
            </Link>
            <span className="header-title">Attendance Dashboard</span>
          </div>
          <div className="header-right">
            <Link to="/attendance/upload" className="upload-button">
              <i className="ri-upload-2-line"></i>
            </Link>
          </div>
        </div>
      </header>
      {/* Header Section End */}

      {/* Attendance Section Start */}
      <section className="fitness-section">
        <div className="custom-container">
          <div className="fitness-card">
            <div className="card-body">
              {/* Tab Navigation */}
              <div className="fitness-tab-row">
                <button 
                  className={`fitness-tab ${tab === 'daily' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('daily')}
                >
                  Daily
                </button>
                <button 
                  className={`fitness-tab ${tab === 'weekly' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`fitness-tab ${tab === 'monthly' ? 'active' : ''}`} 
                  onClick={() => handleTabChange('monthly')}
                >
                  Monthly
                </button>
              </div>

              {/* Date Selection */}
              <div className="date-selection">
                <div className="form-box">
                  <label className="form-label">Select Date</label>
                  {tab === 'daily' && (
                    <DateField value={selectedDate} onChange={setSelectedDate} />
                  )}
                  {tab === 'weekly' && (
                    <DateField value={weekRange.start} onChange={value => handleWeekChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)} />
                  )}
                  {tab === 'monthly' && (
                    <DateField value={selectedMonth + "-01"} onChange={value => setSelectedMonth(value.slice(0, 7))} />
                  )}
                </div>
                <div className="selected-date">
                  {getDateLabel()}
                </div>
              </div>

              {/* Member Filter */}
              <div className="member-filter">
                <div className="form-box">
                  <label className="form-label">Filter by Member</label>
                  <select 
                    value={memberFilter} 
                    onChange={(e) => setMemberFilter(e.target.value)}
                    className="form-control"
                  >
                    <option value="">All Members</option>
                    {uniqueMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Chart Visualization */}
              {chartData && (
                <div className="chart-container">
                  <div className="chart-title">
                    <h6>
                      {tab === 'daily' ? 'Attendance Overview' : 
                       memberFilter ? `Attendance Heatmap - ${uniqueMembers.find(m => m.id === memberFilter)?.name}` : 
                       'Member Attendance Summary'}
                    </h6>
                  </div>
                  <div className="chart-wrapper">
                    {tab === 'daily' ? (
                      <Doughnut 
                        data={chartData}
                        options={{
                          responsive: true,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                            title: {
                              display: true,
                              text: `Total Members: ${summary?.marked || 0} Present, ${(summary?.enrolled || 0) - (summary?.marked || 0)} Absent`
                            }
                          }
                        }}
                      />
                    ) : (
                      <>
                        {chartData?.type === 'heatmap' ? (
                          <AttendanceHeatmap 
                            userAttendanceData={chartData.userAttendanceData || {}}
                            userName={uniqueMembers.find(m => m.id === memberFilter)?.name || 'Selected Member'}
                          />
                        ) : (
                          <Bar 
                            data={chartData}
                            options={{
                              responsive: true,
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                title: {
                                  display: true,
                                  text: `${tab === 'weekly' ? 'Weekly' : 'Monthly'} Attendance Count by Member`
                                },
                                tooltip: {
                                  callbacks: {
                                    title: (context) => {
                                      const index = context[0].dataIndex;
                                      return chartData.fullNames?.[index] || context[0].label;
                                    },
                                    label: (context) => {
                                      return `Total Attendance: ${context.parsed.y}`;
                                    }
                                  }
                                }
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    stepSize: 1
                                  }
                                },
                                x: {
                                  ticks: {
                                    maxRotation: 45,
                                    minRotation: 0
                                  }
                                }
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Summary Card */}
              <div className="fitness-stats-box">
                <div className="fitness-stats-icon">
                  <i className="ri-team-line"></i>
                </div>
                <div className="fitness-stats-content">
                  <h5>{memberFilter ? filteredRecords.length : summary?.marked ?? '-'}</h5>
                  <h6>{memberFilter ? 'Filtered Results' : 'Members Present'}</h6>
                </div>
              </div>
              
              {/* Filter Results Info */}
              {memberFilter && (
                <div className="filter-info">
                  <small className="text-muted">
                    Showing {filteredRecords.length} records for {uniqueMembers.find(m => m.id === memberFilter)?.name || 'Selected Member'}
                  </small>
                </div>
              )}

              {/* Attendance List */}
              <div className="attendance-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID Number</th>
                      <th>Name</th>
                      {tab !== 'daily' && <th>Date</th>}
                      <th>Time In</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr className="loading">
                        <td colSpan={tab !== 'daily' ? 4 : 3}>
                          <div className="spinner"></div>
                          Loading...
                        </td>
                      </tr>
                    )}
                    {error && (
                      <tr className="error">
                        <td colSpan={tab !== 'daily' ? 4 : 3}>
                          <div className="text-danger">{error}</div>
                        </td>
                      </tr>
                    )}
                    {!loading && !error && filteredRecords.length === 0 && (
                      <tr className="empty">
                        <td colSpan={tab !== 'daily' ? 4 : 3}>
                          {memberFilter ? 'No attendance records found for selected member' : 'No records found'}
                        </td>
                      </tr>
                    )}
                    {!loading && !error && filteredRecords.map((rec, idx) => (
                      <tr key={idx}>
                        <td>{rec.serialNo}</td>
                        <td>{rec.name}</td>
                        {tab !== 'daily' && <td>{rec.date ? new Date(rec.date).toLocaleDateString() : '-'}</td>}
                        <td>{rec.timeIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Attendance Section End */}
    </div>
  );
};

export default AttendanceDashboard;
